'use client';

import { useState, useMemo } from 'react';
import Link from 'next/link';
import { useTranslations, useLocale } from 'next-intl';
import { Users, Euro, TrendingDown, ArrowRight } from 'lucide-react';
import { trackLandingCtaClicked } from '@/lib/analytics/posthog';

/**
 * Provisions-Spar-Rechner — interaktiver Linkbait-Calculator (i18n, 7 Locales).
 *
 * Kund:innen-Input: Neukunden/Monat + Ø-Wert der Erstbuchung.
 * Output: Was Provisionsmodelle (Treatwell 35 %, Fresha 20 %) pro Monat
 * kosten — vs. EazyBooking €59 flat, provisionsfrei. Alles client-side.
 *
 * Sätze recherchiert (Juni 2026):
 *  - Treatwell: 35 % auf Erstbuchung jedes Neukunden + €39 Abo
 *  - Fresha: 20 % (min €6) pro Neukunde über Marketplace + ~€45 Abo (5 MA)
 *  - EazyBooking: €59 netto flat, 5 Mitarbeiter inkl., 0 % Provision
 */

const TREATWELL_RATE = 0.35;
const TREATWELL_BASE = 39;
const FRESHA_RATE = 0.2;
const FRESHA_MIN = 6;
const FRESHA_BASE = 45;
const EAZY_FLAT = 59;

const CURRENCY_LOCALE: Record<string, string> = {
  de: 'de-DE',
  en: 'en-IE',
  es: 'es-ES',
  fr: 'fr-FR',
  ru: 'ru-RU',
  tr: 'tr-TR',
  ar: 'ar',
};

function NumberField({
  label,
  icon,
  value,
  setValue,
  min,
  max,
  step = 1,
  suffix,
}: {
  label: string;
  icon: React.ReactNode;
  value: number;
  setValue: (n: number) => void;
  min: number;
  max: number;
  step?: number;
  suffix?: string;
}) {
  return (
    <div>
      <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
        <span className="text-blue-600">{icon}</span>
        {label}
      </label>
      <div className="mt-2 flex items-center gap-3">
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(e) => setValue(Number(e.target.value))}
          className="h-2 flex-1 cursor-pointer appearance-none rounded-full bg-gray-200 accent-blue-600"
        />
        <div className="flex w-24 items-center gap-1 rounded-lg border border-gray-200 px-2 py-1.5">
          <input
            type="number"
            min={min}
            max={max}
            step={step}
            value={value}
            onChange={(e) => setValue(Number(e.target.value))}
            className="w-full bg-transparent text-right text-sm font-semibold text-gray-900 outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none"
          />
          {suffix && <span className="text-xs text-gray-400">{suffix}</span>}
        </div>
      </div>
    </div>
  );
}

/** Vergleichszeile mit proportionalem Balken; `winner` hebt EazyBooking hervor. */
function CostRow({
  name,
  cost,
  max,
  formatted,
  winner,
  note,
}: {
  name: string;
  cost: number;
  max: number;
  formatted: string;
  winner?: boolean;
  note?: string;
}) {
  const pct = max > 0 ? Math.max(4, Math.round((cost / max) * 100)) : 4;
  return (
    <div>
      <div className="flex items-baseline justify-between gap-3">
        <span className={winner ? 'text-sm font-semibold text-gray-900' : 'text-sm text-gray-600'}>
          {name}
          {note && <span className="ml-1.5 text-xs font-normal text-gray-400">{note}</span>}
        </span>
        <span
          className={
            winner
              ? 'text-sm font-bold tabular-nums text-emerald-600'
              : 'text-sm font-semibold tabular-nums text-gray-900'
          }
        >
          {formatted}
        </span>
      </div>
      <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-gray-100">
        <div
          className={`h-full rounded-full ${winner ? 'bg-emerald-500' : 'bg-gray-300'}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

export function CommissionSavingsCalculator() {
  const t = useTranslations('commission');
  const locale = useLocale();
  const [newClients, setNewClients] = useState(25);
  const [avgValue, setAvgValue] = useState(45);

  const eur = useMemo(() => {
    const fmt = new Intl.NumberFormat(CURRENCY_LOCALE[locale] ?? 'de-DE', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0,
    });
    return (n: number) => fmt.format(Math.round(n));
  }, [locale]);

  const result = useMemo(() => {
    const treatwellCost = TREATWELL_BASE + newClients * avgValue * TREATWELL_RATE;
    const freshaCost = FRESHA_BASE + newClients * Math.max(avgValue * FRESHA_RATE, FRESHA_MIN);
    const eazyCost = EAZY_FLAT;
    const savingsVsTreatwell = Math.max(0, treatwellCost - eazyCost);
    const savingsVsFresha = Math.max(0, freshaCost - eazyCost);
    const maxCost = Math.max(treatwellCost, freshaCost, eazyCost);
    return {
      treatwellCost,
      freshaCost,
      eazyCost,
      savingsVsTreatwell,
      savingsVsFresha,
      maxCost,
      yearlyVsTreatwell: savingsVsTreatwell * 12,
    };
  }, [newClients, avgValue]);

  return (
    <div className="mx-auto grid max-w-5xl gap-8 lg:grid-cols-[1.1fr_1fr]">
      {/* Inputs */}
      <div className="rounded-2xl border border-gray-200 bg-white p-6 sm:p-8">
        <h2 className="text-lg font-semibold text-gray-900">{t('inputsTitle')}</h2>
        <p className="mt-1 text-sm text-gray-500">{t('inputsSub')}</p>
        <div className="mt-6 space-y-6">
          <NumberField
            label={t('fields.newClients')}
            icon={<Users className="h-4 w-4" />}
            value={newClients}
            setValue={setNewClients}
            min={1}
            max={150}
          />
          <NumberField
            label={t('fields.avgValue')}
            icon={<Euro className="h-4 w-4" />}
            value={avgValue}
            setValue={setAvgValue}
            min={10}
            max={300}
            step={5}
            suffix="€"
          />
        </div>

        {/* Vergleich */}
        <div className="mt-8 space-y-5 border-t border-gray-100 pt-6">
          <p className="text-sm font-semibold text-gray-900">{t('compareTitle')}</p>
          <CostRow name="Treatwell" note={t('notes.treatwell')} cost={result.treatwellCost} max={result.maxCost} formatted={eur(result.treatwellCost)} />
          <CostRow name="Fresha" note={t('notes.fresha')} cost={result.freshaCost} max={result.maxCost} formatted={eur(result.freshaCost)} />
          <CostRow name="EazyBooking" note={t('notes.eazy')} cost={result.eazyCost} max={result.maxCost} formatted={eur(result.eazyCost)} winner />
        </div>
      </div>

      {/* Result — sticky on desktop */}
      <div className="flex flex-col gap-4 lg:sticky lg:top-24 lg:self-start">
        <div className="rounded-2xl bg-gradient-to-br from-blue-600 to-indigo-600 p-6 text-white sm:p-8">
          <p className="flex items-center gap-1.5 text-sm font-medium text-blue-100">
            <TrendingDown className="h-4 w-4" />
            {t('result.label')}
          </p>
          <div className="mt-2 flex flex-wrap items-baseline gap-2">
            <span className="text-4xl font-bold tracking-tight tabular-nums sm:text-5xl">
              {eur(result.savingsVsTreatwell)}
            </span>
            <span className="text-blue-100">{t('result.perMonth')}</span>
          </div>
          <p className="mt-1 text-sm text-blue-100 tabular-nums">
            {t('result.perYear', { value: eur(result.yearlyVsTreatwell) })}
          </p>

          <div className="mt-6 space-y-3 border-t border-white/20 pt-5 text-sm">
            <div className="flex items-center justify-between gap-3">
              <span className="text-blue-100">{t('result.vsTreatwellLabel')}</span>
              <span className="text-right font-semibold tabular-nums">{eur(result.savingsVsTreatwell)}</span>
            </div>
            <div className="flex items-center justify-between gap-3">
              <span className="text-blue-100">{t('result.vsFreshaLabel')}</span>
              <span className="text-right font-semibold tabular-nums">{eur(result.savingsVsFresha)}</span>
            </div>
            <div className="flex items-center justify-between text-xs text-blue-200">
              <span>{t('result.eazyLabel')}</span>
              <span className="tabular-nums">{eur(EAZY_FLAT)}</span>
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-gray-200 bg-white p-6">
          <p
            className="text-sm text-gray-600 [&_b]:font-semibold [&_b]:text-gray-900"
            dangerouslySetInnerHTML={{ __html: t.raw('pricingNote') as string }}
          />
          <Link
            href="/register?source=commission_calculator"
            onClick={() => trackLandingCtaClicked('commission_calculator')}
            className="mt-4 inline-flex h-11 w-full items-center justify-center gap-1.5 rounded-full bg-gray-950 px-6 text-sm font-semibold text-white transition-colors hover:bg-gray-800"
          >
            {t('ctaButton')}
            <ArrowRight className="h-4 w-4" />
          </Link>
          <p className="mt-3 text-center text-xs text-gray-400">{t('ctaSub')}</p>
        </div>

        <p className="px-2 text-xs leading-relaxed text-gray-400">{t('assumptions')}</p>
      </div>
    </div>
  );
}
